import React, { useEffect, useState, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import { Region } from '../../data/types/region-types';
import { City } from '../../data/types/city-types';
import { useLanguage } from '../../contexts/language';
import { MapMarkerRefs } from './types';
import FundingLabels from './markers/FundingLabels';
import { getCitiesByRegion } from '../../utils/city-utils';
import { fetchJoshuaProjectData } from '../../data/utils/joshua-project-api';
import { regions_mexico } from '../../data/regions';
import RegionMarkersLayer from './markers/RegionMarkersLayer';
import CityMarkersLayer from './markers/CityMarkersLayer';
import { useMarkerCleanup } from './hooks/useMarkerCleanup';

interface MapMarkersProps {
  map: mapboxgl.Map | null;
  regions: Region[];
  cities?: City[];
  filteredRegionIds?: string[];
  selectedRegion: Region | null;
  selectedCity?: City | null;
  onRegionClick: (region: Region) => void;
  onCityClick?: (city: City) => void;
  markerRefs: React.MutableRefObject<MapMarkerRefs>;
  mapZoomLevel?: 'country' | 'region' | 'city';
  is3DMode?: boolean;
  showChiapasCities?: boolean;
  showHidalgoCities?: boolean;
  showGlobalRegions?: boolean;
  isMexicoView?: boolean;
  showFunding?: boolean;
  showLowPresence?: boolean;
  showUnreached?: boolean;
  showAreasOfNeed?: boolean;
}

const MapMarkers: React.FC<MapMarkersProps> = ({ 
  map, 
  regions, 
  cities = [], 
  filteredRegionIds, 
  selectedRegion, 
  selectedCity,
  onRegionClick,
  onCityClick,
  markerRefs,
  mapZoomLevel = 'country',
  is3DMode = false,
  showChiapasCities = false,
  showHidalgoCities = false,
  showGlobalRegions = false,
  isMexicoView = false,
  showFunding = true,
  showLowPresence = true,
  showUnreached = true,
  showAreasOfNeed = true
}) => {
  const { language } = useLanguage();
  const [globalData, setGlobalData] = useState([]);
  
  // Get all Mexico regions
  const allMexicoRegions = useMemo(() => {
    return isMexicoView ? regions_mexico : regions;
  }, [regions, isMexicoView]);
  
  // Set zoom level as a data attribute on the map container
  useEffect(() => {
    if (map && map.getContainer()) {
      const container = map.getContainer();
      container.setAttribute('data-zoom-level', mapZoomLevel);
      
      return () => {
        container.removeAttribute('data-zoom-level');
      };
    }
  }, [map, mapZoomLevel]);
  
  // Handle marker cleanup on component changes
  useMarkerCleanup(
    markerRefs,
    map?.getContainer() || null,
    showFunding,
    showLowPresence,
    showUnreached,
    showAreasOfNeed,
    isMexicoView
  );
  
  // Load global data when in global view
  useEffect(() => {
    if (showGlobalRegions) {
      console.log("MapMarkers: Fetching global data for world view");
      fetchJoshuaProjectData()
        .then(data => {
          console.log("MapMarkers: Loaded global region data:", data.length, "regions");
          setGlobalData(data);
        })
        .catch(error => {
          console.error("MapMarkers: Error loading global region data:", error);
        });
    }
  }, [showGlobalRegions]);
  
  const handleRegionClick = (region: Region) => {
    console.log("MapMarkers: Region clicked:", region.english_name);
    onRegionClick(region);
  };
  
  // Get the appropriate cities to show
  const citiesToShow = useMemo(() => {
    if (selectedRegion && showChiapasCities && selectedRegion.region_id === 'CHIS') {
      return getCitiesByRegion('CHIS');
    } else if (selectedRegion && showHidalgoCities && selectedRegion.region_id === 'HGO') {
      return getCitiesByRegion('HGO');
    } else if (selectedRegion && (mapZoomLevel === 'region' || mapZoomLevel === 'city')) {
      // For any region when zoomed in
      const regionCities = getCitiesByRegion(selectedRegion.region_id);
      console.log(`MapMarkers: Showing cities for ${selectedRegion.english_name}:`, regionCities.length);
      return regionCities;
    }
    return [];
  }, [selectedRegion, showChiapasCities, showHidalgoCities, mapZoomLevel]);

  if (!map) return null;

  console.log("MapMarkers: Rendering with selectedRegion:", selectedRegion?.english_name || "none");
  console.log("MapMarkers: Zoom level:", mapZoomLevel);
  console.log("MapMarkers: Toggle states:", { showFunding, showLowPresence, showUnreached, showAreasOfNeed });

  return (
    <>
      <RegionMarkersLayer 
        map={map}
        regions={regions}
        allMexicoRegions={allMexicoRegions}
        filteredRegionIds={filteredRegionIds}
        selectedRegion={selectedRegion}
        onRegionClick={handleRegionClick}
        markerRefs={markerRefs.current.regions}
        mapZoomLevel={mapZoomLevel}
        showGlobalRegions={showGlobalRegions}
        isMexicoView={isMexicoView}
      />
      
      <CityMarkersLayer
        map={map}
        selectedRegion={selectedRegion}
        selectedCity={selectedCity}
        citiesToShow={citiesToShow}
        onCityClick={onCityClick}
        markerRefs={{
          cities: markerRefs.current.cities,
          churches: markerRefs.current.churches
        }}
        mapZoomLevel={mapZoomLevel}
        showChiapasCities={showChiapasCities}
        showHidalgoCities={showHidalgoCities}
        showGlobalRegions={showGlobalRegions}
        isMexicoView={isMexicoView}
        filteredRegionIds={filteredRegionIds}
        showFunding={showFunding}
      />
      
      <FundingLabels
        map={map}
        regions={regions}
        markerRefs={markerRefs.current.labels}
        mapZoomLevel={mapZoomLevel}
        selectedRegion={selectedRegion}
        selectedCity={selectedCity}
        showGlobalRegions={showGlobalRegions}
        globalData={globalData}
        isMexicoView={isMexicoView}
        showFunding={showFunding}
      />
    </>
  );
};

export default MapMarkers;
